/**
 * A flown path the map can draw (#581).
 *
 * # Why this file is shaped the way it is
 *
 * The type that used to live here was hand-written, and it shared **not one
 * field name** with the endpoint that produces a track. It declared
 * `traveledRoute` as `[lat, lon]` pairs, a `plannedRoute` no part of the API has
 * ever sent, and `startPoint` / `endPoint` objects the server does not describe.
 * It replaced a `Mission` type that was invented the same way (#516), and its
 * own comment predicted this:
 *
 * > When that lands, the real shape will come from the backend and this type
 * > should be derived from a contract fixture rather than hand-written again.
 *
 * So {@link ApiFlightTrackCollection} below is the GeoJSON the server really
 * emits, pinned as a fixture by
 * `internal/api/handlers/drone_contract_test.go` and read back in
 * `tests/services/apiContract.test.ts`. Change the response and the Go test
 * fails; regenerate it and the UI test fails until this file is taught the new
 * shape (§7 of `.claude/testing-guidelines.md`).
 *
 * # The axis swap
 *
 * **GeoJSON coordinates are `[longitude, latitude]`. Leaflet positions are
 * `[latitude, longitude]`.** They are reversed, and nothing about a pair of
 * numbers says which one it is. The old type documented the wrong order against
 * the real endpoint, so drawing a Warsaw flight straight from the wire would
 * have put it in the Arabian Sea — and near the equator, which is where the
 * simulator used to fly (#561), it would have looked plausible.
 *
 * {@link toFlightTrack} is the one place the swap happens, and it is asserted
 * explicitly on both sides of the wire: here, and in the producer's own test
 * (`internal/api/handlers/track_distance_test.go`).
 */

// ---------------------------------------------------------------------------
// The wire shape — GeoJSON, as the server sends it
// ---------------------------------------------------------------------------

/**
 * One position fix as GeoJSON orders it: `[longitude, latitude, altitude]`.
 *
 * A tuple rather than `number[]`, so reading `[0]` as a latitude is a type
 * error rather than a wrong map.
 */
export type GeoJsonPosition = [longitude: number, latitude: number, altitude: number];

/** `properties` of the single feature a track response carries. */
export interface ApiFlightTrackProperties {
  drone_id: string;
  start_time: string;
  end_time: string;
  distance_m: number;
  duration_s: number;
  max_altitude: number;
}

/** The `LineString` geometry of a flown track. */
export interface ApiFlightTrackGeometry {
  type: string;
  coordinates: GeoJsonPosition[];
}

/** One GeoJSON feature: a flown track. */
export interface ApiFlightTrackFeature {
  type: string;
  geometry: ApiFlightTrackGeometry;
  properties: ApiFlightTrackProperties;
}

/**
 * The whole response from `GET /api/v1/drones/{droneID}/telemetry/track`.
 *
 * `features` is an **empty array** for a drone that has not flown — not a
 * missing key and not null. That is the usual response today, so
 * `features[0]` is the dereference this module exists to stop anyone writing.
 */
export interface ApiFlightTrackCollection {
  type: string;
  features: ApiFlightTrackFeature[];
}

// ---------------------------------------------------------------------------
// The shape the map draws
// ---------------------------------------------------------------------------

/** A position in Leaflet's order: `[latitude, longitude]`. */
export type LeafletPosition = [latitude: number, longitude: number];

/**
 * A flown path, in the units and order the map and the panel read.
 *
 * Only what something actually renders. Per-point altitude is on the wire and
 * deliberately not carried here: the view shows the flight's ceiling, which is
 * `maxAltitude` from the server's own `max_altitude`, and a second altitude
 * series with no reader would be the #631 defect pointing the other way (§8).
 */
export interface FlightTrack {
  /**
   * The drone that flew it. One track per drone per time window, so this
   * doubles as the identity the clustering layer keys on.
   */
  id: string;
  /** Where it has been, as `[lat, lon]` pairs — Leaflet's order. */
  traveledRoute: LeafletPosition[];
  /** ISO timestamps, straight from the server. */
  startedAt: string;
  endedAt: string;
  /** Metres flown, summed leg by leg by the server (#667). */
  distanceM: number;
  durationS: number;
  /** The highest altitude reached, in metres. */
  maxAltitude: number;
}

/**
 * Convert the server's GeoJSON into the track the map draws, or `null` when
 * there is no flight to draw.
 *
 * `null` rather than an empty track, because "this drone has not flown" is a
 * state the UI has to say out loud, and an empty polyline says nothing. It is
 * the common case today.
 *
 * The data shape is validated rather than trusted (rule 10 of `ui/CLAUDE.md`):
 * a coordinate that is not at least a longitude and a latitude is dropped, and
 * a track left with fewer than two points is no track. The server should never
 * send either, but a malformed fix must not take the fleet map down with it —
 * which is what an unchecked `last_known_position` did in #508.
 */
export const toFlightTrack = (
  collection: ApiFlightTrackCollection | null | undefined
): FlightTrack | null => {
  const feature = collection?.features?.[0];
  if (!feature) {
    return null;
  }

  const traveledRoute = (feature.geometry?.coordinates ?? []).flatMap(
    (position): LeafletPosition[] => {
      const [longitude, latitude] = position ?? [];
      if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
        return [];
      }
      // The swap, in the only place it happens: GeoJSON gives longitude
      // first, Leaflet wants latitude first.
      return [[latitude, longitude]];
    }
  );

  if (traveledRoute.length < 2) {
    return null;
  }

  const properties = feature.properties;

  return {
    id: properties.drone_id,
    traveledRoute,
    startedAt: properties.start_time,
    endedAt: properties.end_time,
    distanceM: properties.distance_m,
    durationS: properties.duration_s,
    maxAltitude: properties.max_altitude,
  };
};
